import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

interface WishlistContextType {
  wishlistIds: string[];
  isInWishlist: (productId: string) => boolean;
  toggleWishlist: (productId: string) => void;
  wishlistCount: number;
  isLoading: boolean;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, user } = useAuth();
  const [wishlistIds, setWishlistIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (!user) {
      setWishlistIds([]);
      return;
    }
    setIsLoading(true);
    const { data, error } = await supabase
      .from("wishlists")
      .select("product_id")
      .eq("user_id", user.id);

    if (!error && data) {
      setWishlistIds(data.map((w) => w.product_id));
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const isInWishlist = useCallback(
    (productId: string) => wishlistIds.includes(productId),
    [wishlistIds]
  );

  const toggleWishlist = useCallback(
    async (productId: string) => {
      if (!isAuthenticated || !user) {
        toast.error("Please log in to save favorites");
        return;
      }

      const inList = wishlistIds.includes(productId);

      if (inList) {
        // Optimistic removal
        setWishlistIds((prev) => prev.filter((id) => id !== productId));
        const { error } = await supabase
          .from("wishlists")
          .delete()
          .eq("user_id", user.id)
          .eq("product_id", productId);

        if (error) {
          setWishlistIds((prev) => [...prev, productId]);
          toast.error("Failed to remove from wishlist");
        } else {
          toast.success("Removed from wishlist");
        }
      } else {
        // Optimistic addition
        setWishlistIds((prev) => [...prev, productId]);
        const { error } = await supabase
          .from("wishlists")
          .insert({ user_id: user.id, product_id: productId });

        if (error) {
          setWishlistIds((prev) => prev.filter((id) => id !== productId));
          toast.error("Failed to add to wishlist");
        } else {
          toast.success("Added to wishlist");
        }
      }
    },
    [isAuthenticated, user, wishlistIds]
  );

  return (
    <WishlistContext.Provider
      value={{
        wishlistIds,
        isInWishlist,
        toggleWishlist,
        wishlistCount: wishlistIds.length,
        isLoading,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};
